import { AsyncPipe } from '@angular/common';
import type { AfterViewInit } from '@angular/core';
import {
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  inject,
  input,
  linkedSignal,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import type { FormValueControl } from '@angular/forms/signals';
import { TranslatePipe } from '@ngx-translate/core';
import {
  BehaviorSubject,
  debounceTime,
  map,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { ONYX_INPUT_DEBOUNCE_TIME } from '../../../constants';
import type { OnyxOption } from '../../../directives/interfaces';
import { ONYX_FOCUSABLE } from '../../../directives/onyx-auto-focus.directive';
import { OnyxDropdownDirective } from '../../../directives/onyx-dropdown.directive';
import { OnyxTooltipDirective } from '../../../directives/onyx-tooltip.directive';
import { AddressHelper } from '../../../helpers/address.helper';
import { CoordinatesHelper } from '../../../helpers/coordinates.helper';
import { OverflowHelper } from '../../../helpers/overflow.helper';
import { OnyxBaseFormControlComponent } from '../../../internal/components/onyx-base-form-control/onyx-base-form-control.component';
import { I18N_NAMESPACE } from '../../../internal/constants';
import { OnyxHighlightPipe } from '../../../pipes/onyx-highlight.pipe';
import { OnyxAddressService } from '../../../services/onyx-address.service';
import { OnyxModalService } from '../../../services/onyx-modal.service';
import { OnyxClearButtonComponent } from '../../buttons/onyx-clear-button/onyx-clear-button.component';
import { OnyxIconButtonComponent } from '../../buttons/onyx-icon-button/onyx-icon-button.component';
import { OnyxErrorComponent } from '../../error/onyx-error/onyx-error.component';
import { OnyxIconComponent } from '../../icons/onyx-icon/onyx-icon.component';
import { OnyxInputLabelComponent } from '../../labels/onyx-input-label/onyx-input-label.component';
import { OnyxTextFieldComponent } from '../../text-field/onyx-text-field/onyx-text-field.component';
import type { OnyxTooltipContext } from '../../tooltip/interfaces';
import { OnyxAddressType } from '../enums';
import type { OnyxAddress, OnyxCustomAddress } from '../interfaces';
import type { OnyxAddressPickerModalData } from '../onyx-address-picker-modal/onyx-address-picker-modal.component';
import { OnyxAddressPickerModalComponent } from '../onyx-address-picker-modal/onyx-address-picker-modal.component';

const I18N = `${I18N_NAMESPACE}.address`;

@Component({
  selector: 'onyx-address-input',
  imports: [
    OnyxClearButtonComponent,
    OnyxDropdownDirective,
    OnyxErrorComponent,
    OnyxHighlightPipe,
    OnyxIconComponent,
    OnyxInputLabelComponent,
    TranslatePipe,
    OnyxTextFieldComponent,
    OnyxIconButtonComponent,
    AsyncPipe,
    OnyxTooltipDirective,
  ],
  templateUrl: './onyx-address-input.component.html',
  styleUrl: './onyx-address-input.component.scss',
  host: {
    '[style.width]': `width()`,
    '[style.gap.px]': `gap()`,
    '(focusout)': `restoreOnFocusout()`,
  },
  providers: [
    {
      provide: ONYX_FOCUSABLE,
      useExisting: forwardRef(() => OnyxAddressInputComponent),
    },
  ],
})
export class OnyxAddressInputComponent
  extends OnyxBaseFormControlComponent
  implements FormValueControl<OnyxAddress | null>, AfterViewInit
{
  protected readonly overflowHelper = inject(OverflowHelper);
  private readonly onyxAddressService = inject(OnyxAddressService);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly modalService = inject(OnyxModalService);

  public readonly value = model<OnyxAddress | null>(null);
  public readonly label = input<string>();
  public readonly hint = input<OnyxTooltipContext>();
  public readonly types = input([OnyxAddressType.CUSTOM]);
  public readonly size = input<32 | 40>(40);
  public readonly limit = input(5);
  public readonly width = input('100%');
  public readonly notFoundMessage = input(`${I18N}.noResults`);
  public readonly showAddOption = input<boolean | 'value'>(false);
  public readonly addOptionMessage = input<string>(`${I18N}.addNew`);
  public readonly showPicker = input(true);
  public readonly showApartment = input(false);
  public readonly showRequired = input(true);
  public readonly apartmentWidth = input('40%');
  public readonly gap = input(8);
  public readonly placeholder = input(`${I18N}.placeholder`);
  public readonly showErrors = input(true);
  public readonly emptyAddressOptions = input<OnyxOption<OnyxAddress>[] | null>(
    null,
  );

  public readonly nameChange = output<string>();
  public readonly addOption = output<string | undefined>();

  protected readonly I18N = I18N;

  protected readonly OnyxAddressType = OnyxAddressType;

  protected loading = signal(false);
  protected query = signal('');
  protected apartmentNumber = linkedSignal(
    () => this.value()?.apartmentNumber ?? null,
  );
  protected expanded = signal(false);

  protected inputValue = computed(() => {
    const address = this.value();
    if (!address) return '';

    if (address.type === OnyxAddressType.POINT_OF_INTEREST) {
      return `${address.poiName}, ${address.label}`;
    }
    return address.label;
  });

  protected subheading = computed(() =>
    this.query() || this.emptyAddressOptions()?.length
      ? `${I18N}.suggestions`
      : `${I18N}.lastSearches`,
  );

  private _addresses$ = new BehaviorSubject<OnyxOption<OnyxAddress>[]>([]);
  protected get addresses$() {
    return this._addresses$.asObservable();
  }

  private inputElementRef =
    viewChild<ElementRef<HTMLInputElement>>('inputElement');
  private request$ = new Subject<void>();

  constructor() {
    super();

    this.request$
      .pipe(
        tap(() => this.loading.set(true)),
        debounceTime(ONYX_INPUT_DEBOUNCE_TIME),
        switchMap(() => {
          const query = this.query();
          if (query) {
            const coordinates = CoordinatesHelper.fromString(query);
            return this.onyxAddressService
              .geocode(
                this.types(),
                coordinates ? CoordinatesHelper.toString(coordinates) : query,
                this.limit(),
              )
              .pipe(
                map(({ items }) =>
                  items.map((address) => ({
                    name: address.label,
                    value: address,
                  })),
                ),
              );
          }

          const emptyOptions = this.emptyAddressOptions();
          if (emptyOptions?.length) return of(emptyOptions);

          return this.onyxAddressService
            .getRecentSearches(this.types(), this.limit())
            .pipe(
              map((addresses) =>
                addresses.map((address) => ({
                  name: address.label,
                  value: address,
                })),
              ),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((addresses) => {
        this._addresses$.next(addresses);
        this.loading.set(false);
      });

    effect(() => {
      this.query();
      this.emptyAddressOptions();
      this.request$.next();
    });

    effect(() => {
      const value = this.value();
      if (!value || !this.showApartment()) return;

      if (value.apartmentNumber !== this.apartmentNumber()) {
        this.changeValue(value, this.apartmentNumber());
      }
    });
  }

  public override ngOnInit(): void {
    super.ngOnInit();

    toObservable(this.value, { injector: this.injector })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.transformValue(value));
  }

  public override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.updateInputValue();
  }

  public focus(): void {
    this.inputElementRef()?.nativeElement.focus();
  }

  protected restoreOnFocusout(): void {
    setTimeout(() => {
      if (this.expanded()) return;
      if (this.elementRef.nativeElement.contains(document.activeElement)) {
        return;
      }

      this.query.set('');
      this.updateInputValue();
      this.touch.emit();
    });
  }

  protected changeQuery(event: Event): void {
    let { value } = event.target as HTMLInputElement;
    value = value.trim();

    if (!value) {
      this.clearValue();
      return;
    }

    this.query.set(value);
    this.expanded.set(true);
  }

  protected changeValue(
    value: OnyxAddress | null,
    apartmentNumber?: string | null,
  ): void {
    if (value?.houseNumber && apartmentNumber !== undefined) {
      value = {
        ...value,
        apartmentNumber,
      };
      value.label = AddressHelper.composeLabel(value);
    }

    if (value) {
      this.onyxAddressService.addToRecentSearches(value).subscribe();

      if ('name' in value) {
        if (value.name) this.nameChange.emit(value.name);
        delete (value as any).name;
      }
    }

    this.value.set(value);
    this.apartmentNumber.set(value?.apartmentNumber ?? null);
    this.touch.emit();
  }

  protected clearValue(): void {
    this.query.set('');
    this.value.set(null);
    this.apartmentNumber.set(null);
    this.expanded.set(false);
    this.updateInputValue();
    this.touch.emit();
  }

  protected openPicker(): void {
    if (this.disabled()) return;

    const address = this.value();
    const readonly = address?.type === OnyxAddressType.POINT_OF_INTEREST;
    if (!readonly && !this.types().includes(OnyxAddressType.CUSTOM)) return;

    this.expanded.set(false);
    this.modalService
      .open<OnyxAddressPickerModalData, OnyxCustomAddress>(
        OnyxAddressPickerModalComponent,
        { address, readonly },
      )
      .result.subscribe((address) => {
        if (address) this.changeValue(address);
      });
  }

  private transformValue(value: OnyxAddress | null): void {
    if (value && 'name' in value) {
      if (value.name) this.nameChange.emit(value.name);
      delete (value as { name?: string }).name;
      this.value.set(value);
    }

    this.apartmentNumber.set(value?.apartmentNumber ?? null);
    this.updateInputValue();
  }

  private updateInputValue(): void {
    const inputElement = this.inputElementRef();
    if (!inputElement) return;

    inputElement.nativeElement.value = this.inputValue();
  }
}
